import React from 'react';
import { css } from '@emotion/react';

import isMobile from '$/utils/isMobile';

import { CloseIcon } from '$/images/Icons';
import { Truncate } from '$/components/Truncate/Truncate';

import { Mentionable } from '../types';
import { useDropdownContext } from '../../Input/Basics/DropdownComponent';
import { SearchInputFilter } from '../../Input/Filters/SearchInputFilter';

interface MentionListProps {
  mentions: Mentionable[];
  onClickMention: (mention: Mentionable) => void;
  onClose?: () => void;
  isRtl?: boolean;
}
export const MentionList: React.FC<MentionListProps> = ({ mentions, onClickMention, onClose }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const dropdownContext = useDropdownContext();
  const [search, setSearch] = React.useState('');

  const onInputChange = React.useCallback((v: string) => {
    setSearch(v);
  }, []);

  const mentionsFiltered = React.useMemo(() => {
    return mentions.filter((mention) => mention.name.toLowerCase().includes(search.toLowerCase()));
  }, [mentions, search]);

  React.useEffect(() => {
    if (dropdownContext?.open) {
      setSearch('');
      inputRef.current?.focus();
    }
  }, [dropdownContext?.open]);

  return (
    <div
      ref={ref}
      tabIndex={0}
      css={css`
        padding: 1rem;
        width: ${isMobile ? 'calc(100vw - 2rem)' : '26rem'};
        max-width: ${isMobile ? 'calc(100vw - 2rem)' : '100%'};
      `}
    >
      <div className='flex flex-row justify-end items-center mb-1e'>
        <CloseIcon className='w-1e h-1e' onClick={onClose} />
      </div>
      <SearchInputFilter
        inputRef={inputRef}
        className='mb-1e'
        placeholder='Search for a merge field'
        isInputOpen
        onChange={onInputChange}
        value={search}
      />
      <div tabIndex={0} className='max-h-12e md:max-h-20e overflow-y-auto'>
        {mentionsFiltered.map((mention) => (
          <div
            className='hover:bg-gray-200 cursor-pointer p-2 rounded'
            key={mention.name}
            onClick={(e) => {
              e.preventDefault();
              onClickMention(mention);
            }}
          >
            <div className='mb-1 text-lg font-semibold'>{mention.name}</div>
            <Truncate lines={3} type='div'>
              {mention.explanation && (
                <div dangerouslySetInnerHTML={{ __html: mention.explanation }} />
              )}
            </Truncate>
          </div>
        ))}
        {mentionsFiltered.length === 0 && (
          <div className='p-2e mt-1e rounded-lg text-center border'>No merge field</div>
        )}
      </div>
    </div>
  );
};
